import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { useTheme, Box } from '@mui/material';

// CSS Fallback particles when WebGL is not available
function CSSParticles({ isDark }: { isDark: boolean }) {
  const particleCount = 50;
  const particles = Array.from({ length: particleCount }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 2 + Math.random() * 4,
    delay: Math.random() * 5,
    duration: 8 + Math.random() * 4,
  }));

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {particles.map((p) => (
        <Box
          key={p.id}
          sx={{
            position: 'absolute',
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            backgroundColor: isDark ? '#7dd3fc' : '#38bdf8',
            opacity: isDark ? 0.6 : 0.4,
            animation: `float ${p.duration}s ease-in-out ${p.delay}s infinite`,
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0)', opacity: isDark ? 0.6 : 0.4 },
              '50%': { transform: 'translateY(-10px)', opacity: isDark ? 0.8 : 0.6 },
            },
          }}
        />
      ))}
    </Box>
  );
}

export default function ParticleBackground() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [webglFailed, setWebglFailed] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => {
    if (!containerRef.current || webglFailed) return;

    const container = containerRef.current;

    const initTimeout = requestAnimationFrame(() => {
      const width = container.clientWidth;
      const height = container.clientHeight;

      if (width < 10 || height < 10) return;

      try {
        // Test WebGL availability
        const testCanvas = document.createElement('canvas');
        const gl = testCanvas.getContext('webgl') || testCanvas.getContext('experimental-webgl');
        if (!gl) {
          setWebglFailed(true);
          return;
        }

        // ── Globe settings ──────────────────────────
        const GLOBE_RADIUS = 220;
        const NODE_COUNT = 1600; // points distributed on the sphere
        const ARC_COUNT = 16; // concurrent traveling arcs
        const accentColor = isDark ? '#7dd3fc' : '#38bdf8';

        // Scene setup
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(60, width / height, 1, 5000);
        camera.position.z = 620;

        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        renderer.setSize(width, height);
        // Point sizes in the shaders are expressed in physical (framebuffer) pixels.
        // On Retina screens (DPR=2) the framebuffer is 2× larger, so a fixed point
        // covers only half the CSS area and the additive-blended dots look faded.
        // Feed the ratio into the shaders so points scale up to the same on-screen size.
        const pixelRatio = Math.min(window.devicePixelRatio, 2);
        renderer.setPixelRatio(pixelRatio);
        container.appendChild(renderer.domElement);

        // Parent group so globe points + arcs rotate together
        const globe = new THREE.Group();
        scene.add(globe);

        // ── Distribute nodes on the sphere (Fibonacci sphere) ──
        const goldenAngle = Math.PI * (3 - Math.sqrt(5));
        const nodes: THREE.Vector3[] = [];
        const nodePositions = new Float32Array(NODE_COUNT * 3);
        for (let i = 0; i < NODE_COUNT; i++) {
          const y = 1 - (i / (NODE_COUNT - 1)) * 2; // 1 → -1
          const r = Math.sqrt(1 - y * y);
          const theta = goldenAngle * i;
          const x = Math.cos(theta) * r;
          const z = Math.sin(theta) * r;
          const v = new THREE.Vector3(x, y, z).multiplyScalar(GLOBE_RADIUS);
          nodes.push(v);
          nodePositions[i * 3] = v.x;
          nodePositions[i * 3 + 1] = v.y;
          nodePositions[i * 3 + 2] = v.z;
        }

        const nodeGeometry = new THREE.BufferGeometry();
        nodeGeometry.setAttribute('position', new THREE.BufferAttribute(nodePositions, 3));

        // Soft round points via shader (no texture needed)
        const nodeMaterial = new THREE.ShaderMaterial({
          uniforms: {
            color: { value: new THREE.Color(accentColor) },
            uPixelRatio: { value: pixelRatio },
          },
          vertexShader: `
            uniform float uPixelRatio;
            void main() {
              vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
              gl_PointSize = 3.3 * uPixelRatio * (300.0 / -mvPosition.z);
              gl_Position = projectionMatrix * mvPosition;
            }
          `,
          fragmentShader: `
            uniform vec3 color;
            void main() {
              vec2 c = gl_PointCoord - vec2(0.5);
              float dist = length(c);
              if (dist > 0.5) discard;
              float alpha = smoothstep(0.5, 0.15, dist) * ${isDark ? '1.0' : '0.7'};
              gl_FragColor = vec4(color, alpha);
            }
          `,
          transparent: true,
          depthWrite: false,
          blending: isDark ? THREE.AdditiveBlending : THREE.NormalBlending,
        });

        const nodePoints = new THREE.Points(nodeGeometry, nodeMaterial);
        globe.add(nodePoints);

        // Faint wireframe sphere underneath for "structure"
        const wireGeometry = new THREE.SphereGeometry(GLOBE_RADIUS * 0.985, 36, 24);
        const wireMaterial = new THREE.MeshBasicMaterial({
          color: new THREE.Color(accentColor),
          wireframe: true,
          transparent: true,
          opacity: isDark ? 0.05 : 0.04,
          depthWrite: false,
        });
        globe.add(new THREE.Mesh(wireGeometry, wireMaterial));

        // ── Dotted continents (subtle landmass silhouette) ──
        // Sample an equirectangular land/water mask: land is dark, ocean is white.
        let cancelled = false;
        let landGeometry: THREE.BufferGeometry | null = null;
        let landMaterial: THREE.ShaderMaterial | null = null;
        let landPoints: THREE.Points | null = null;

        const landImg = new Image();
        landImg.onload = () => {
          if (cancelled) return;
          const cw = landImg.width;
          const ch = landImg.height;
          const sampleCanvas = document.createElement('canvas');
          sampleCanvas.width = cw;
          sampleCanvas.height = ch;
          const ctx = sampleCanvas.getContext('2d');
          if (!ctx) return;
          ctx.drawImage(landImg, 0, 0);
          const data = ctx.getImageData(0, 0, cw, ch).data;

          const deg2rad = Math.PI / 180;
          const STEP = 1.6; // sampling step in degrees → controls dot density
          const LAND_RADIUS = GLOBE_RADIUS * 1.004; // sit just above the node sphere
          const coords: number[] = [];
          for (let lat = -82; lat <= 82; lat += STEP) {
            for (let lng = -180; lng < 180; lng += STEP) {
              const px = Math.min(cw - 1, Math.floor(((lng + 180) / 360) * cw));
              const py = Math.min(ch - 1, Math.floor(((90 - lat) / 180) * ch));
              const brightness = data[(py * cw + px) * 4]; // grayscale → R channel
              if (brightness < 90) {
                // land
                const phi = (90 - lat) * deg2rad;
                const theta = (lng + 180) * deg2rad;
                coords.push(
                  -LAND_RADIUS * Math.sin(phi) * Math.cos(theta),
                  LAND_RADIUS * Math.cos(phi),
                  LAND_RADIUS * Math.sin(phi) * Math.sin(theta)
                );
              }
            }
          }

          landGeometry = new THREE.BufferGeometry();
          landGeometry.setAttribute(
            'position',
            new THREE.BufferAttribute(new Float32Array(coords), 3)
          );
          landMaterial = new THREE.ShaderMaterial({
            uniforms: {
              color: { value: new THREE.Color(accentColor) },
              uPixelRatio: { value: pixelRatio },
            },
            vertexShader: `
              uniform float uPixelRatio;
              void main() {
                vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                gl_PointSize = 3.0 * uPixelRatio * (300.0 / -mvPosition.z);
                gl_Position = projectionMatrix * mvPosition;
              }
            `,
            fragmentShader: `
              uniform vec3 color;
              void main() {
                vec2 c = gl_PointCoord - vec2(0.5);
                float dist = length(c);
                if (dist > 0.5) discard;
                float alpha = smoothstep(0.5, 0.15, dist) * ${isDark ? '1.0' : '0.8'};
                gl_FragColor = vec4(color, alpha);
              }
            `,
            transparent: true,
            depthWrite: false,
            blending: isDark ? THREE.AdditiveBlending : THREE.NormalBlending,
          });
          landPoints = new THREE.Points(landGeometry, landMaterial);
          globe.add(landPoints);
        };
        landImg.src = '/textures/earth-water.png';

        // ── Traveling arcs between random nodes ──────
        const ARC_SEGMENTS = 60; // vertices per arc curve
        const ARC_POINTS = ARC_SEGMENTS + 1;

        interface Arc {
          line: THREE.Line;
          head: number; // current head position (in vertices)
          speed: number; // vertices per second
          tail: number; // visible trailing length (in vertices)
        }

        const arcMaterial = new THREE.LineBasicMaterial({
          color: new THREE.Color(accentColor),
          transparent: true,
          opacity: isDark ? 1.0 : 0.75,
          blending: isDark ? THREE.AdditiveBlending : THREE.NormalBlending,
          depthWrite: false,
        });

        // Build a Bézier arc that bulges outward from the globe surface
        const buildArcCurve = (geometry: THREE.BufferGeometry) => {
          const a = nodes[Math.floor(Math.random() * NODE_COUNT)];
          const b = nodes[Math.floor(Math.random() * NODE_COUNT)];
          // Lift the control point above the surface; more lift for distant pairs
          const mid = a.clone().add(b).multiplyScalar(0.5);
          const distFactor = a.distanceTo(b) / (GLOBE_RADIUS * 2); // 0 → 1
          const lift = GLOBE_RADIUS * (1.15 + distFactor * 0.6);
          const control = mid.normalize().multiplyScalar(lift);
          const curve = new THREE.QuadraticBezierCurve3(a.clone(), control, b.clone());
          const pts = curve.getPoints(ARC_SEGMENTS);
          const arr = new Float32Array(ARC_POINTS * 3);
          for (let i = 0; i < pts.length; i++) {
            arr[i * 3] = pts[i].x;
            arr[i * 3 + 1] = pts[i].y;
            arr[i * 3 + 2] = pts[i].z;
          }
          geometry.setAttribute('position', new THREE.BufferAttribute(arr, 3));
        };

        const arcs: Arc[] = [];
        for (let i = 0; i < ARC_COUNT; i++) {
          const geometry = new THREE.BufferGeometry();
          buildArcCurve(geometry);
          const line = new THREE.Line(geometry, arcMaterial);
          line.frustumCulled = false;
          globe.add(line);
          arcs.push({
            line,
            head: Math.random() * ARC_POINTS, // stagger so they don't sync
            speed: 17 + Math.random() * 6,
            tail: 9 + Math.floor(Math.random() * 8),
          });
        }

        // Mouse tracking (subtle parallax tilt)
        let mouseX = 0,
          mouseY = 0;
        const handleMouseMove = (event: MouseEvent) => {
          mouseX = (event.clientX / window.innerWidth) * 2 - 1;
          mouseY = (event.clientY / window.innerHeight) * 2 - 1;
        };
        window.addEventListener('mousemove', handleMouseMove);

        // ── Animation (frame-rate independent) ───────
        let animationId: number;
        let lastTime = performance.now();
        const SPIN_SPEED = 0.12; // radians per second

        const animate = () => {
          animationId = requestAnimationFrame(animate);

          const currentTime = performance.now();
          const deltaTime = Math.min((currentTime - lastTime) / 1000, 0.1);
          lastTime = currentTime;

          // Continuous spin + gentle mouse-driven tilt
          globe.rotation.y += SPIN_SPEED * deltaTime;
          const targetTiltX = mouseY * 0.35;
          const targetTiltZ = mouseX * 0.1;
          globe.rotation.x += (targetTiltX - globe.rotation.x) * 0.04;
          globe.rotation.z += (targetTiltZ - globe.rotation.z) * 0.04;

          // Advance each arc; reveal only a traveling segment
          for (const arc of arcs) {
            arc.head += arc.speed * deltaTime;
            const start = Math.max(0, Math.floor(arc.head - arc.tail));
            const end = Math.min(ARC_POINTS, Math.floor(arc.head));
            arc.line.geometry.setDrawRange(start, Math.max(0, end - start));

            // Recycle with new endpoints once it travels past the end
            if (arc.head - arc.tail > ARC_POINTS) {
              buildArcCurve(arc.line.geometry);
              arc.head = 0;
              arc.speed = 17 + Math.random() * 6;
              arc.tail = 9 + Math.floor(Math.random() * 8);
            }
          }

          renderer.render(scene, camera);
        };

        animate();

        // Resize handler
        const handleResize = () => {
          const newWidth = container.clientWidth;
          const newHeight = container.clientHeight;
          if (newWidth > 0 && newHeight > 0) {
            camera.aspect = newWidth / newHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(newWidth, newHeight);
          }
        };
        window.addEventListener('resize', handleResize);

        // Cleanup
        const cleanup = () => {
          cancelled = true;
          cancelAnimationFrame(animationId);
          window.removeEventListener('mousemove', handleMouseMove);
          window.removeEventListener('resize', handleResize);
          if (container.contains(renderer.domElement)) {
            container.removeChild(renderer.domElement);
          }
          nodeGeometry.dispose();
          nodeMaterial.dispose();
          wireGeometry.dispose();
          wireMaterial.dispose();
          arcMaterial.dispose();
          for (const arc of arcs) arc.line.geometry.dispose();
          if (landPoints) globe.remove(landPoints);
          landGeometry?.dispose();
          landMaterial?.dispose();
          renderer.dispose();
        };

        (container as unknown as { _cleanup?: () => void })._cleanup = cleanup;
      } catch (error) {
        console.error('ParticleBackground initialization error:', error);
        setWebglFailed(true);
      }
    });

    return () => {
      cancelAnimationFrame(initTimeout);
      const cleanup = (container as unknown as { _cleanup?: () => void })._cleanup;
      if (cleanup) cleanup();
    };
  }, [isDark, webglFailed]);

  if (webglFailed) {
    return <CSSParticles isDark={isDark} />;
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
        opacity: 0.85,
      }}
    />
  );
}
